import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from "../lib/supabase";
import { useZipLookup } from '../hooks/useZipLookup';

export default function BasicInfo({ onComplete }: { onComplete?: () => void }) {
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [zipError, setZipError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const MAX_CHARS = 500;

  const { suggestions, resolvedLocation } = useZipLookup(location);
  const showDropdown = isFocused && suggestions.length > 0;

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) {
      setBio(text);
    }
  };

  const validateZipCode = (zip: string): boolean => {
    const zipRegex = /^\d{5}$/;
    return zipRegex.test(zip.trim());
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setLocation(digits);
    setHighlightIndex(-1);
    if (zipError) {
      setZipError('');
    }
  };

  const selectSuggestion = (zip: string) => {
    setLocation(zip);
    setHighlightIndex(-1);
    setZipError('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex].postal_code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleNext = async () => {
    if (!location.trim() || !bio.trim()) {
      alert('Please fill in both location and bio before proceeding.');
      return;
    }

    if (!validateZipCode(location)) {
      setZipError('Please enter a valid 5-digit US ZIP code');
      return;
    }
    const supabase = getSupabaseClient();

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      alert('Not logged in');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('guide-basic-info', {
        body: {
          location,
          bio,
        },
      });

      if (error) {
        console.error('Error saving basic info:', error);
        throw new Error(error.message || 'Failed to invoke edge function');
      }

      if (!data?.success) {
        const errorMessage = data?.error || 'Failed to save basic info';
        console.error('Edge function returned error:', data);
        throw new Error(errorMessage);
      }

      // Trigger the refresh function instead of navigating
      if (onComplete) {
        onComplete();
      } else {
        navigate('/onboarding');
      }
    } catch (err) {
      console.error('Error in handleNext:', err);
      alert(err instanceof Error ? err.message : 'Failed to save basic info.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 md:mx-0 md:px-16">
      {/* Title and description */}
      <div className="mb-8">
        <h1 className="text-[42px] leading-[44px] font-medium text-[#304031] font-recoleta mb-6">
          Let's Get to Know You
        </h1>
        <p className="text-[20px] leading-[1.6] text-[#304031]">
          This is where we get a quick sense of who you are, where you practice, and the type of support you offer. You'll have a chance to build a more detailed seeker-facing profile later — for now, we just need the essentials to begin your vetting process.
        </p>
      </div>

      {/* Where are you located? */}
      <div className="mb-6">
        <label className="block text-[20px] leading-[1.6] text-[#304031] mb-3">
          What is your ZIP / Postal Code?
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="Enter ZIP / Postal Code"
            value={location}
            onChange={handleLocationChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-controls={showDropdown ? 'zip-suggestions' : undefined}
            aria-activedescendant={
              showDropdown && highlightIndex >= 0
                ? `zip-option-${highlightIndex}`
                : undefined
            }
            className={`w-full border rounded-[10px] px-3 py-4 text-base text-[#304031] placeholder:text-[#707c8b] focus:outline-none focus:ring-2 bg-[#fffcef] ${
              zipError
                ? 'border-red-400 focus:ring-red-400'
                : 'border-[#304031]/30 focus:ring-[#304031]'
            } ${resolvedLocation && !zipError ? 'pr-40' : ''}`}
          />
          {resolvedLocation && !zipError && (
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-[#707c8b]">
              {resolvedLocation}
            </span>
          )}

          {showDropdown && (
            <ul
              id="zip-suggestions"
              role="listbox"
              className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-[#304031]/20 bg-[#fffcef] shadow-lg"
            >
              {suggestions.map((s, i) => (
                <li
                  key={s.postal_code}
                  id={`zip-option-${i}`}
                  role="option"
                  aria-selected={i === highlightIndex}
                  className={`cursor-pointer px-5 py-3 text-base transition ${
                    i === highlightIndex
                      ? 'bg-[#304031]/10 text-[#2C2731]'
                      : 'text-[#2C2731] hover:bg-[#304031]/5'
                  }`}
                  onMouseDown={e => {
                    e.preventDefault();
                    selectSuggestion(s.postal_code);
                  }}
                >
                  <span className="font-medium">{s.postal_code}</span>
                  <span className="ml-2 text-sm text-[#707c8b]">
                    {s.city}, {s.state}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {zipError && (
          <p className="mt-2 text-sm text-red-600">{zipError}</p>
        )}
        <p className="mt-2 text-sm text-[#304031]/70">
          This helps us match you with seekers who can travel to you, are in your region for virtual support, or are in your timezone. Your location does not limit your options — it simply helps us tailor your matches.
        </p>
      </div>

      {/* Brief Bio */}
      <div className="mb-6">
        <label className="block text-[20px] leading-[1.6] text-[#304031] mb-3">Short Introduction</label>
        <div className="relative">
          <textarea
            rows={5}
            placeholder="Use this space to briefly introduce yourself to us. This is a quick snapshot to aid in our vetting of your profile — you'll be able to expand on your story and approach later in your seeker-facing full profile. Share a little about your background and what brought you to this work."
            value={bio}
            onChange={handleBioChange}
            className="w-full border border-[#304031]/30 rounded-[10px] px-3 py-4 text-base text-[#304031] placeholder:text-[#707c8b] focus:outline-none focus:ring-2 focus:ring-[#304031] bg-[#fffcef] resize-none"
          />
        </div>
        <div className="mt-2 text-sm text-[#304031]/70 text-right">
          {bio.length}/{MAX_CHARS} characters
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-4 items-center">
        <button
          onClick={() => navigate('/onboarding')}
          className="inline-flex items-center gap-2 rounded-full border border-[#304031] px-6 py-3 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 rounded-full bg-[#304031] text-[#fef6d6] px-6 py-3 text-base font-semibold transition hover:bg-[#304031]/90"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
